import React, { useState, useMemo, useId, useEffect, useRef } from 'react';
import './VehicleValueHill.css';

// ─── analytics ────────────────────────────────────────────────────────────────

const trackCMV = (event, props) => {
  try {
    window.analytics?.track(event, { platform: 'web', ...props });
  } catch (_) {}
};

// ─── formatting ────────────────────────────────────────────────────────────────

const CAD = n =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);

// ─── zone definitions ──────────────────────────────────────────────────────────

const ZONES = [
  {
    key: 'too-low',
    label: 'Below Market',
    icon: '😟',
    color: '#d71920',
    msg:
      'Your price is below the current estimated market range. You may be leaving money on the table.',
    aria: 'Warning: price is below market.',
  },
  {
    key: 'good-price',
    label: 'Competitive Price',
    icon: '😐',
    color: '#b08200',
    msg: 'Attracts buyers quickly. A solid, competitive price that balances speed and return.',
    aria: 'Good: price is competitive.',
  },
  {
    key: 'best-value',
    label: 'Market Value',
    icon: '😊',
    color: '#07883f',
    msg: 'Sweet spot! This is the ideal listing price — maximum buyer interest at a fair return.',
    aria: 'Excellent: you are at market value.',
  },
  {
    key: 'above-market',
    label: 'Above Market',
    icon: '😟',
    color: '#e66b12',
    msg: 'May take longer to sell. Buyers are comparing to similar vehicles priced lower.',
    aria: 'Caution: price is above the market range.',
  },
  {
    key: 'too-high',
    label: 'Significantly Above Market',
    icon: '😟',
    color: '#d71920',
    msg: 'Your price is above the current estimated market range and may reduce buyer interest.',
    aria: 'Warning: price is significantly above market.',
  },
];

// ─── zone classification ───────────────────────────────────────────────────────

function classify(price, fairMin, fairMax, mktLow, mktHigh) {
  const belowGap = fairMin - mktLow;
  const aboveGap = mktHigh - fairMax;
  if (price < fairMin - belowGap * 0.45) return 'too-low';
  if (price < fairMin) return 'good-price';
  if (price <= fairMax) return 'best-value';
  if (price <= fairMax + aboveGap * 0.45) return 'above-market';
  return 'too-high';
}

// ─── SVG layout constants ──────────────────────────────────────────────────────

const VBW = 900; // viewBox width
const VBH = 295; // viewBox height
const PAD = 40; // x-padding
const HB = 185; // hill bottom y
const HT = 18; // hill top y (peak)
const HH = HB - HT; // hill height in SVG units

// ─── component ────────────────────────────────────────────────────────────────

/**
 * VehicleValueHill
 *
 * Interactive hill-shaped market-value visualiser.
 *
 * Props:
 *   marketLow          — absolute lowest market price (CMV marketLow)
 *   marketHigh         — absolute highest market price (CMV marketHigh)
 *   fairRangeMin       — fair market range lower bound (CMV fairRangeLow)
 *   fairRangeMax       — fair market range upper bound (CMV fairRangeHigh)
 *   estimatedValue     — computed vehicle CMV estimate (shown prominently)
 *   initialAskingPrice — optional; defaults to estimatedValue
 *   trendLabel         — optional trend text shown on Fair Market Range card, e.g. "+8.3% vs last 30 days"
 *   vehicleId          — optional vehicle ID for analytics
 *   onPriceSelect      — optional callback(price) when user finalises a price
 *
 * Example with live CMV data:
 *   const { cmv } = await fetch('/api/cmv?vin=…').then(r => r.json());
 *   <VehicleValueHill
 *     marketLow={cmv.marketLow}
 *     marketHigh={cmv.marketHigh}
 *     fairRangeMin={cmv.fairRangeLow}
 *     fairRangeMax={cmv.fairRangeHigh}
 *     estimatedValue={cmv.estimatedValue}
 *     initialAskingPrice={listing.price}
 *   />
 */
export function VehicleValueHill({
  marketLow = 20000,
  marketHigh = 36000,
  fairRangeMin = 26900,
  fairRangeMax = 29900,
  estimatedValue = 28450,
  initialAskingPrice,
  trendLabel,
  vehicleId,
  onPriceSelect,
}) {
  const rawId = useId();
  // sanitise for use in SVG id attributes (remove colons etc.)
  const uid = rawId.replace(/[^a-zA-Z0-9_-]/g, '');

  const clampedInit = Math.min(
    Math.max(initialAskingPrice ?? estimatedValue, marketLow),
    marketHigh
  );
  const [price, setPrice] = useState(clampedInit);
  const prevZoneRef = useRef(null);

  // cmv_viewed — fires once on mount
  useEffect(() => {
    trackCMV('cmv_viewed', { vehicleId, estimatedCMV: estimatedValue });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── hill math ───────────────────────────────────────────────────────────────

  const hillCenter = (fairRangeMin + fairRangeMax) / 2;
  const hillSigma = (marketHigh - marketLow) * 0.2;

  // zone threshold prices
  const belowGap = fairRangeMin - marketLow;
  const aboveGap = marketHigh - fairRangeMax;
  const zoneTL = fairRangeMin - belowGap * 0.45; // too-low / good-price
  const zoneAM = fairRangeMax + aboveGap * 0.45; // above-market / too-high

  // coordinate helpers (fresh functions, inline in memo where needed)
  const toX = p => PAD + ((VBW - 2 * PAD) * (p - marketLow)) / (marketHigh - marketLow);
  const toY = p => {
    const z = (p - hillCenter) / hillSigma;
    return HB - HH * Math.exp(-0.5 * z * z);
  };

  // hill SVG path — only recomputed when props change, not on slider move
  const hillPath = useMemo(() => {
    const N = 400;
    const span = marketHigh - marketLow;
    const iw = VBW - 2 * PAD;
    const cx = p => PAD + (iw * (p - marketLow)) / span;
    const cy = p => {
      const z = (p - hillCenter) / hillSigma;
      return HB - HH * Math.exp(-0.5 * z * z);
    };
    let d = `M ${cx(marketLow)},${HB}`;
    for (let i = 0; i <= N; i++) {
      const p = marketLow + span * (i / N);
      d += ` L ${cx(p).toFixed(2)},${cy(p).toFixed(2)}`;
    }
    d += ` L ${cx(marketHigh)},${HB} Z`;
    return d;
  }, [marketLow, marketHigh, hillCenter, hillSigma]);

  // gradient stops — driven entirely by props, zero hard-coded business logic
  const gradStops = useMemo(() => {
    const pct = p => `${(((p - marketLow) / (marketHigh - marketLow)) * 100).toFixed(2)}%`;
    return [
      { offset: '0%', color: '#d71920' },
      { offset: pct(zoneTL), color: '#f26622' },
      { offset: pct(fairRangeMin), color: '#f3bb17' },
      { offset: pct(hillCenter), color: '#07883f' },
      { offset: pct(fairRangeMax), color: '#07883f' },
      { offset: pct(zoneAM), color: '#f26622' },
      { offset: '100%', color: '#d71920' },
    ];
  }, [marketLow, marketHigh, zoneTL, fairRangeMin, fairRangeMax, hillCenter, zoneAM]);

  // slider track gradient (matches hill colours)
  const sliderGrad = useMemo(() => {
    const pct = p => `${(((p - marketLow) / (marketHigh - marketLow)) * 100).toFixed(1)}%`;
    return [
      `#d71920 0%`,
      `#f26622 ${pct(zoneTL)}`,
      `#f3bb17 ${pct(fairRangeMin)}`,
      `#07883f ${pct(hillCenter)}`,
      `#07883f ${pct(fairRangeMax)}`,
      `#f26622 ${pct(zoneAM)}`,
      `#d71920 100%`,
    ].join(',');
  }, [marketLow, marketHigh, zoneTL, fairRangeMin, fairRangeMax, hillCenter, zoneAM]);

  // ── current state ───────────────────────────────────────────────────────────

  const zone = classify(price, fairRangeMin, fairRangeMax, marketLow, marketHigh);
  const zoneConf = ZONES.find(z => z.key === zone);

  // cmv_status_changed — fires when zone changes after first render
  useEffect(() => {
    if (prevZoneRef.current !== null && prevZoneRef.current !== zone) {
      trackCMV('cmv_status_changed', {
        vehicleId,
        status: zone,
        selectedPrice: price,
        estimatedCMV: estimatedValue,
      });
    }
    prevZoneRef.current = zone;
  }, [zone]); // eslint-disable-line react-hooks/exhaustive-deps

  // marker position on hill surface
  const mx = toX(price);
  const my = toY(price);

  // ── popup positioning ───────────────────────────────────────────────────────

  const PW = 152,
    PH = 64,
    PA = 9;
  const popAbove = my - PH - PA - 4 >= HT + 4;
  const rawPopX = mx - PW / 2;
  const popX = Math.max(PAD, Math.min(rawPopX, VBW - PAD - PW));
  const popY = popAbove ? my - PH - PA - 4 : my + PA + 4;
  // arrow tip and base
  const arrowTipY = popAbove ? my - 5 : my + 5;
  const arrowBaseY = popAbove ? popY + PH : popY;
  const arrowBX = Math.max(popX + 16, Math.min(mx, popX + PW - 16));
  const arrowPts = `${mx},${arrowTipY} ${arrowBX - 8},${arrowBaseY} ${arrowBX + 8},${arrowBaseY}`;

  // ── zone label positions ────────────────────────────────────────────────────

  const labelCenters = [
    { key: 'too-low', p: (marketLow + zoneTL) / 2 },
    { key: 'good-price', p: (zoneTL + fairRangeMin) / 2 },
    { key: 'best-value', p: hillCenter },
    { key: 'above-market', p: (fairRangeMax + zoneAM) / 2 },
    { key: 'too-high', p: (zoneAM + marketHigh) / 2 },
  ];

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <article className="vehicle-value-hill" aria-label="CMV — Canadian Market Value visualiser">
      {/* ── HEADER ── */}
      <div className="vehicle-value-hill__header">
        <div>
          <p className="vehicle-value-hill__eyebrow">Your CMV</p>
          <p
            className="vehicle-value-hill__estimated-value"
            aria-label={`CMV — Canadian Market Value: ${CAD(estimatedValue)}`}
          >
            {CAD(estimatedValue)}
          </p>
          <span className="vehicle-value-hill__verified">✓ Verified Canadian Market Data</span>
        </div>
        <aside className="vehicle-value-hill__trust-box" aria-label="CMV data trust indicator">
          <span className="vehicle-value-hill__trust-icon" aria-hidden="true">
            🛡
          </span>
          <div>
            <strong>CMV — Canadian Market Value</strong>
            <span>Based on real Canadian market data</span>
          </div>
        </aside>
      </div>

      {/* ── STATUS ── */}
      <div className="vehicle-value-hill__status-summary">
        <span
          className={`vehicle-value-hill__status-badge vehicle-value-hill__status-badge--${zone}`}
          role="status"
          aria-live="polite"
          aria-label={zoneConf.aria}
        >
          <span aria-hidden="true">{zoneConf.icon}</span> {zoneConf.label}
        </span>
        {zone === 'best-value' && <p>Sweet Spot – Best Time to Sell!</p>}
      </div>

      {/* ── SVG HILL ── */}
      <div className="vehicle-value-hill__chart-wrapper" aria-hidden="true">
        <svg
          viewBox={`0 0 ${VBW} ${VBH}`}
          className="vehicle-value-hill__chart"
          role="presentation"
          xmlns="http://www.w3.org/2000/svg"
          style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
        >
          <defs>
            <linearGradient id={`${uid}g`} x1="0" y1="0" x2="1" y2="0">
              {gradStops.map((s, i) => (
                <stop key={i} offset={s.offset} stopColor={s.color} />
              ))}
            </linearGradient>
          </defs>

          {/* Hill fill */}
          <path d={hillPath} fill={`url(#${uid}g)`} opacity={0.91} />

          {/* Baseline */}
          <line x1={PAD} y1={HB} x2={VBW - PAD} y2={HB} className="vehicle-value-hill__baseline" />

          {/* Zone faces + labels below baseline */}
          {labelCenters.map(({ key, p }) => {
            const conf = ZONES.find(z => z.key === key);
            const lx = toX(p);
            return (
              <g key={key}>
                <text
                  x={lx}
                  y={HB + 26}
                  textAnchor="middle"
                  className={`vehicle-value-hill__zone-face vehicle-value-hill__zone-face--${key}`}
                >
                  {conf.icon}
                </text>
                <text
                  x={lx}
                  y={HB + 50}
                  textAnchor="middle"
                  className={`vehicle-value-hill__zone-label vehicle-value-hill__zone-label--${key}`}
                >
                  {conf.label}
                </text>
              </g>
            );
          })}

          {/* Estimated value reference dot */}
          <circle
            cx={toX(estimatedValue)}
            cy={toY(estimatedValue)}
            r={5}
            className="vehicle-value-hill__estimated-dot"
          />

          {/* Dashed vertical line from marker to baseline */}
          <line x1={mx} y1={my} x2={mx} y2={HB} className="vehicle-value-hill__marker-line" />

          {/* Price popup */}
          <g className="vehicle-value-hill__popup">
            <polygon points={arrowPts} className="vehicle-value-hill__popup-background" />
            <rect
              x={popX}
              y={popY}
              width={PW}
              height={PH}
              rx={10}
              ry={10}
              className="vehicle-value-hill__popup-background"
            />
            <text
              x={popX + PW / 2}
              y={popY + 26}
              textAnchor="middle"
              className="vehicle-value-hill__popup-price"
            >
              {CAD(price)}
            </text>
            <text
              x={popX + PW / 2}
              y={popY + 48}
              textAnchor="middle"
              className="vehicle-value-hill__popup-status"
            >
              {zoneConf.label}
            </text>
          </g>

          {/* Marker */}
          <circle
            cx={mx}
            cy={my}
            r={17}
            className={`vehicle-value-hill__marker vehicle-value-hill__marker--${zone}`}
          />
          <circle cx={mx} cy={my} r={6} className="vehicle-value-hill__marker-centre" />
        </svg>
      </div>

      {/* ── SLIDER ── */}
      <section className="vehicle-value-hill__slider-section">
        <label htmlFor={`${uid}range`} className="vehicle-value-hill__slider-label">
          Drag to compare your asking price
        </label>
        <input
          id={`${uid}range`}
          type="range"
          className="vehicle-value-hill__range-input"
          min={marketLow}
          max={marketHigh}
          step={50}
          value={price}
          onChange={e => {
            const p = Number(e.target.value);
            setPrice(p);
            trackCMV('cmv_slider_moved', {
              vehicleId,
              selectedPrice: p,
              estimatedCMV: estimatedValue,
            });
          }}
          onMouseUp={e => {
            const p = Number(e.target.value);
            trackCMV('cmv_price_selected', {
              vehicleId,
              selectedPrice: p,
              status: zone,
              estimatedCMV: estimatedValue,
            });
            onPriceSelect?.(p);
          }}
          onTouchEnd={e => {
            const p = Number(e.target.value);
            trackCMV('cmv_price_selected', {
              vehicleId,
              selectedPrice: p,
              status: zone,
              estimatedCMV: estimatedValue,
            });
            onPriceSelect?.(p);
          }}
          aria-label="Adjust asking price"
          aria-valuetext={`${CAD(price)} — ${zoneConf.label}`}
          aria-describedby={`${uid}help`}
          style={{
            background: `linear-gradient(90deg,${sliderGrad})`,
            '--thumb-color': zoneConf.color,
          }}
        />
        <div className="vehicle-value-hill__slider-values">
          <span>{CAD(marketLow)}</span>
          <strong aria-live="polite" aria-label={`Selected price: ${CAD(price)}`}>
            {CAD(price)}
          </strong>
          <span>{CAD(marketHigh)}</span>
        </div>
        <p id={`${uid}help`} className="vehicle-value-hill__slider-help">
          Use ← → arrow keys for fine control &nbsp;·&nbsp; Fair market range: {CAD(fairRangeMin)} –{' '}
          {CAD(fairRangeMax)}
        </p>
      </section>

      {/* ── EXPLANATION ── */}
      <div
        className={`vehicle-value-hill__explanation vehicle-value-hill__explanation--${zone}`}
        role="status"
        aria-live="polite"
        aria-label={`${zoneConf.label}: ${zoneConf.msg}`}
      >
        <span className="vehicle-value-hill__explanation-icon" aria-hidden="true">
          {zoneConf.icon}
        </span>
        <div>
          <strong>{zoneConf.label}</strong>
          <p>{zoneConf.msg}</p>
        </div>
      </div>

      {/* ── MARKET SUMMARY ── */}
      <div
        className="vehicle-value-hill__market-summary"
        role="region"
        aria-label="Market price summary"
      >
        <article>
          <div
            className="vehicle-value-hill__summary-icon vehicle-value-hill__summary-icon--low"
            aria-hidden="true"
          >
            ↓
          </div>
          <div>
            <small>Market Low</small>
            <strong style={{ color: '#d71920' }}>{CAD(marketLow)}</strong>
            <p>You may be undervaluing.</p>
          </div>
        </article>

        <article>
          <div
            className="vehicle-value-hill__summary-icon vehicle-value-hill__summary-icon--fair"
            aria-hidden="true"
          >
            ◎
          </div>
          <div>
            <small>CMV Fair Range</small>
            <strong style={{ color: '#07883f' }}>
              {CAD(fairRangeMin)} – {CAD(fairRangeMax)}
            </strong>
            {trendLabel ? (
              <p className="vehicle-value-hill__trend-label">{trendLabel}</p>
            ) : (
              <p>This is the sweet spot range.</p>
            )}
          </div>
        </article>

        <article>
          <div
            className="vehicle-value-hill__summary-icon vehicle-value-hill__summary-icon--high"
            aria-hidden="true"
          >
            ↑
          </div>
          <div>
            <small>Market High</small>
            <strong style={{ color: '#d71920' }}>{CAD(marketHigh)}</strong>
            <p>May be overpriced.</p>
          </div>
        </article>
      </div>

      <p className="vehicle-value-hill__footer-note">
        💡 CMV prices update daily from real Canadian market sales data.
      </p>
    </article>
  );
}

export default VehicleValueHill;
